// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.
// frontend/src/components/compliance/modals/RiskQueueModal.jsx

import React from "react";
import {
  ActionGroup,
  Alert,
  Button,
  EmptyState,
  Metric,
} from "../../../design-system/components";
import ModalShell from "./shared/ModalShell";
import ModalSection from "./shared/ModalSection";

export default function RiskQueueModal({
  open,
  framework,
  filters,
  riskQueueFilter,
  setRiskQueueFilter,
  riskQueueFilterCounts,
  filteredActiveRiskQueueRows,
  loadedActiveRiskQueueCount,
  loadedRemainingRiskCount,
  remainingTopRiskCount,
  activeRiskQueueFilterLabel,
  selectedRemainingRiskCount,
  visibleNoTaskRiskCount,
  allVisibleNoTaskRisksSelected,
  selectVisibleNoTaskRisks,
  clearRemainingRiskSelection,
  remainingRiskBulkCreating,
  remainingRiskBulkNotice,
  remainingRiskBulkError,
  handleBulkCreateRemainingRiskTasks,
  filteredTopRiskRows,
  filteredRemainingRiskRows,
  renderRiskQueueRow,
  onClose,
}) {
  if (!open) return null;

  const unloadedCount = Math.max(
    0,
    remainingTopRiskCount - loadedRemainingRiskCount
  );
  const noTaskCount = riskQueueFilterCounts?.no_task || 0;
  const highCount = riskQueueFilterCounts?.high || 0;

  const bulkActions = [
    {
      key: "select-visible",
      label: allVisibleNoTaskRisksSelected
        ? "Clear Visible"
        : "Select Visible No Task",
      variant: "secondary",
      size: "small",
      onClick: selectVisibleNoTaskRisks,
      disabled:
        visibleNoTaskRiskCount === 0 || remainingRiskBulkCreating,
    },
    {
      key: "clear-selection",
      label: "Clear Selection",
      variant: "tertiary",
      size: "small",
      onClick: clearRemainingRiskSelection,
      disabled:
        selectedRemainingRiskCount === 0 &&
        !remainingRiskBulkNotice &&
        !remainingRiskBulkError,
    },
    {
      key: "create-tasks",
      label: `Create ${
        selectedRemainingRiskCount || ""
      } Task${selectedRemainingRiskCount === 1 ? "" : "s"}`.trim(),
      variant: "primary",
      size: "small",
      onClick: handleBulkCreateRemainingRiskTasks,
      disabled:
        selectedRemainingRiskCount === 0 ||
        remainingRiskBulkCreating,
      loading: remainingRiskBulkCreating,
      loadingLabel: "Creating tasks…",
    },
  ];

  return (
    <ModalShell
      open
      ariaLabel="Active risk queue"
      eyebrow="OASYS · Risk Queue"
      title="Active Risk Queue"
      subtitle="Prioritize the highest-risk conditions, review their operational context, and convert untracked findings into governed remediation work."
      onClose={onClose}
      maxWidth="1180px"
      accentBorder="rgba(74, 222, 128, 0.45)"
      bodyClassName="oasys-risk-queue-modal"
    >
      <div className="oasys-risk-queue-summary">
        <div
          className="oasys-risk-filter-bar"
          role="group"
          aria-label="Filter active risks"
        >
          {filters.map((filter) => {
            const count = riskQueueFilterCounts[filter.key] || 0;
            const active = riskQueueFilter === filter.key;
            const disabled = count === 0 && filter.key !== "all";

            return (
              <Button
                key={filter.key}
                size="small"
                variant={active ? "primary" : "secondary"}
                onClick={() => setRiskQueueFilter(filter.key)}
                disabled={disabled}
                aria-pressed={active}
                title={`${filter.label}: ${count} remaining risk${
                  count === 1 ? "" : "s"
                }`}
              >
                {filter.label} {count}
              </Button>
            );
          })}
        </div>

        <div className="oasys-risk-metric-grid">
          <Metric
            label="Shown"
            value={filteredActiveRiskQueueRows.length}
            supportingText={`of ${loadedActiveRiskQueueCount} loaded`}
            tone="informational"
            size="compact"
          />
          <Metric
            label="Framework"
            value={framework}
            supportingText="selected scope"
            tone="informational"
            size="compact"
          />
          <Metric
            label="High Severity"
            value={highCount}
            supportingText="high-priority findings"
            tone={highCount > 0 ? "danger" : "success"}
            size="compact"
          />
          <Metric
            label="No Task"
            value={noTaskCount}
            supportingText="can be converted"
            tone={noTaskCount > 0 ? "warning" : "success"}
            size="compact"
          />
          <Metric
            label="Not Loaded"
            value={unloadedCount}
            supportingText="additional conditions"
            tone={unloadedCount > 0 ? "warning" : "informational"}
            size="compact"
          />
        </div>

        <section className="oasys-risk-bulk-panel">
          <div>
            <h3>Bulk Task Creation</h3>
            <p>
              {selectedRemainingRiskCount > 0
                ? `${selectedRemainingRiskCount} untracked risk${
                    selectedRemainingRiskCount === 1 ? "" : "s"
                  } selected for conversion.`
                : `${visibleNoTaskRiskCount} visible untracked risk${
                    visibleNoTaskRiskCount === 1 ? "" : "s"
                  } can be converted into governed remediation tasks.`}
            </p>
          </div>

          <ActionGroup
            actions={bulkActions}
            align="end"
            density="compact"
          />
        </section>

        {remainingRiskBulkNotice ? (
          <Alert tone="success" title="Tasks created">
            {remainingRiskBulkNotice}
          </Alert>
        ) : null}

        {remainingRiskBulkError ? (
          <Alert tone="danger" title="Task creation failed">
            {remainingRiskBulkError}
          </Alert>
        ) : null}
      </div>

      {loadedActiveRiskQueueCount === 0 ? (
        <EmptyState
          title="No active risks are loaded"
          description={`No active risk rows are currently available for ${framework}.`}
          size="compact"
        />
      ) : (
        <>
          <ModalSection
            title="Top Risk Activity"
            description="The highest-priority conditions currently influencing posture."
            badge={`${filteredTopRiskRows.length} shown`}
            badgeTone={
              filteredTopRiskRows.length > 0 ? "warn" : "default"
            }
          >
            <div className="oasys-risk-list">
              {filteredTopRiskRows.length === 0 ? (
                <EmptyState
                  title="No top risks match this filter"
                  description={`No Top Risks match the ${activeRiskQueueFilterLabel} filter.`}
                  size="compact"
                />
              ) : (
                filteredTopRiskRows.map((risk, idx) =>
                  renderRiskQueueRow(risk, idx, "top")
                )
              )}
            </div>
          </ModalSection>

          <ModalSection
            title="Remaining Active Risk Activity"
            description="Additional active findings loaded for the selected framework."
            badge={`${filteredRemainingRiskRows.length} shown`}
            badgeTone={
              filteredRemainingRiskRows.length > 0 ? "default" : "ok"
            }
          >
            <div className="oasys-risk-list">
              {filteredRemainingRiskRows.length === 0 ? (
                <EmptyState
                  title="No additional risks to show"
                  description={
                    riskQueueFilter !== "all"
                      ? `No remaining active risks match the ${activeRiskQueueFilterLabel} filter.`
                      : "No additional active risk rows were returned beyond the Top Risks preview."
                  }
                  size="compact"
                />
              ) : (
                filteredRemainingRiskRows.map((risk, idx) =>
                  renderRiskQueueRow(risk, idx, "remaining")
                )
              )}
            </div>
          </ModalSection>
        </>
      )}
    </ModalShell>
  );
}
